import { useState } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle, Loader2, Phone, Mail, User, Building, MessageSquare } from "lucide-react";
import { trackEvent, trackException } from "@/lib/firebase";

export default function ServiceInquiryForm({ serviceName, serviceId, compact = false }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
    service: serviceName || "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone is required";
    } else if (!/^[6-9]\d{9}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = "Invalid phone number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://finease-crm.vercel.app';

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      trackEvent("service_inquiry_validation_failed", {
        service_name: serviceName || formData.service || "",
        service_id: serviceId || "",
      });
      return;
    }

    setIsSubmitting(true);
    trackEvent("service_inquiry_submit_attempt", {
      service_name: serviceName || formData.service || "",
      service_id: serviceId || "",
      compact,
    });

    try {
      const response = await fetch(`${API_BASE_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          service: formData.service,
          message: formData.message,
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Submission failed');
      }

      setIsSubmitted(true);
      trackEvent("service_inquiry_submitted", {
        service_name: formData.service || serviceName || "",
        service_id: serviceId || "",
        has_company: Boolean(formData.company),
        has_message: Boolean(formData.message),
      });
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        message: "",
        service: serviceName || "",
      });
    } catch (error) {
      console.error("Lead submission error:", error);
      trackEvent("service_inquiry_submit_failed", {
        service_name: serviceName || formData.service || "",
        service_id: serviceId || "",
        reason: error?.message || "unknown",
      });
      trackException("service_inquiry_request_failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`bg-[#0A0A0A] border border-white/10 rounded-2xl ${compact ? 'p-6' : 'p-8'}`}
      >
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#00C896]/10 border border-[#00C896]/20 flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-[#00C896]" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">Thank You!</h3>
          <p className="text-slate-400 mb-6">
            Our team will contact you within 2 hours during business hours.
          </p>
          <button
            onClick={() => setIsSubmitted(false)}
            className="text-[#00C896] text-sm font-medium hover:underline"
          >
            Submit another inquiry
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`bg-[#0A0A0A] border border-white/10 rounded-2xl ${compact ? 'p-6' : 'p-8'}`}
    >
      {!compact && (
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-white mb-2">
            Get Started Today
          </h3>
          <p className="text-slate-400 text-sm">
            Fill the form below and our expert will call you within 2 hours.
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Name Field */}
        <div>
          <label className="block text-sm text-slate-400 mb-1.5">Full Name *</label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your name"
              className={`w-full bg-white/5 border ${errors.name ? 'border-red-500' : 'border-white/10'} rounded-lg pl-10 pr-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-[#00C896]/50 transition-colors`}
            />
          </div>
          {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
        </div>

        {/* Email Field */}
        <div>
          <label className="block text-sm text-slate-400 mb-1.5">Email Address *</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your@email.com"
              className={`w-full bg-white/5 border ${errors.email ? 'border-red-500' : 'border-white/10'} rounded-lg pl-10 pr-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-[#00C896]/50 transition-colors`}
            />
          </div>
          {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
        </div>

        {/* Phone Field */}
        <div>
          <label className="block text-sm text-slate-400 mb-1.5">Phone Number *</label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="10-digit mobile number"
              className={`w-full bg-white/5 border ${errors.phone ? 'border-red-500' : 'border-white/10'} rounded-lg pl-10 pr-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-[#00C896]/50 transition-colors`}
            />
          </div>
          {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
        </div>

        {/* Company Field (Optional) */}
        {!compact && (
          <div>
            <label className="block text-sm text-slate-400 mb-1.5">Company Name (Optional)</label>
            <div className="relative">
              <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="Your company name"
                className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-[#00C896]/50 transition-colors"
              />
            </div>
          </div>
        )}

        {/* Service (Hidden or Display) */}
        {serviceName && (
          <div>
            <label className="block text-sm text-slate-400 mb-1.5">Service</label>
            <input
              type="text"
              name="service"
              value={serviceName}
              readOnly
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white/70 cursor-not-allowed"
            />
          </div>
        )}

        {/* Message Field */}
        {!compact && (
          <div>
            <label className="block text-sm text-slate-400 mb-1.5">Your Requirements (Optional)</label>
            <div className="relative">
              <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-slate-500" />
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us about your specific requirements..."
                rows={3}
                className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-[#00C896]/50 transition-colors resize-none"
              />
            </div>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-[#00C896] text-black font-semibold py-3.5 rounded-lg hover:bg-[#00E6AC] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              Get Free Consultation
            </>
          )}
        </button>

        {/* Trust Note */}
        <p className="text-xs text-slate-500 text-center">
          By submitting, you agree to our{" "}
          <a href="/privacy" className="text-[#00C896] hover:underline">Privacy Policy</a>
          {" "}and{" "}
          <a href="/terms" className="text-[#00C896] hover:underline">Terms of Service</a>
        </p>
      </form>
    </motion.div>
  );
}
